import type { LucideIcon } from 'lucide-react';

/** Sidebar entry contributed by a feature module (design §5.3). */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** `'exact'` activates only on the exact path; `'prefix'` (default) also matches children. */
  match?: 'prefix' | 'exact';
}

interface FeatureNavModule {
  nav?: NavItem[];
}

/**
 * Nav contributions live in `src/features/<name>/nav.ts` (one per feature) and
 * are discovered with the same glob idea as `src/features/registry.ts` uses for
 * routes — so adding a nav item never edits the shell or the layout.
 *
 * They deliberately do NOT live in `routes.tsx`: this file is imported by
 * `AppLayout`, which `routes.tsx` imports, so globbing the route modules here
 * would make the layout read a module that is still mid-initialization.
 * Feature nav modules import types only (erased at runtime), keeping the graph
 * acyclic.
 */
const featureModules = import.meta.glob<FeatureNavModule>('../../features/*/nav.ts', {
  eager: true,
});

/** Feature-contributed nav entries, in feature-directory order (deterministic). */
export function collectNavItems(): NavItem[] {
  return Object.entries(featureModules)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([, mod]) => (Array.isArray(mod?.nav) ? mod.nav : []));
}
